// Remote Sessions Guidance Page
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import { usePageMeta } from "../components/shared/SEOHelper";

const RemoteSessionsPage = () => {
  usePageMeta({
    title: "Remote Yoga Sessions Guide | Online Classes | Annelie Lily",
    description: "Guidance for remote yoga sessions with Annelie Lily. Learn how to prepare for online yoga classes, technical requirements, and health & safety guidelines.",
    canonical: "https://annelielily.com/remote-sessions"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      
      <main className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl text-neutral-800 mb-4">
            Guidance for Remote Sessions
          </h1>
          <p className="text-neutral-600 mb-8">
            Maximising your experience of remote yoga sessions
          </p>
          
          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
            <section>
              <p>
                Being in a remote class is a different experience to being physically present with a teacher in a group setting. 
                You may be attending remotely because it is not possible to physically attend your regular local class, or you 
                may attend a remote class because it offers the opportunity to work with a teacher who ordinarily lives too far 
                away for you to attend regularly.
              </p>
              <p>
                Remote teaching is offered in a number of formats. You may be attending a live streamed secure session that 
                enables interaction between you, the teacher and the other participants, or you may be watching or listening 
                to a pre-recorded session.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Health Questionnaire and Assessment</h2>
              <p>
                Before taking part in a remote session you must have completed a health questionnaire and disclaimer. If you 
                are an existing student you do not need to complete an additional health questionnaire but you must sign a 
                disclaimer for remote learning. If you are a new student, you must complete the health questionnaire, which 
                includes a disclaimer, and go through a brief online assessment interview. This is a one-off process.
              </p>
              <p>
                A verbal screening before each session will still take place and this is when you should bring to the attention 
                of your teacher anything that is new and current, in relation to your health, for the session you are about to 
                participate in.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Important Disclaimer</h2>
              <div className="bg-gold/10 border border-gold/20 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-neutral-800 mb-2">Your Responsibility</p>
                    <p className="text-sm">
                      Remote learning has insurance implications and responsibilities for you as a student/participant. 
                      Teachers are fully insured to teach remotely, either live streamed or pre-recorded, on a secure platform, 
                      to a private audience. However, they are unable to risk assess the space in which you are practising, 
                      or you, to the same degree that is possible in face to face teaching.
                    </p>
                    <p className="text-sm mt-3">
                      By participating in remote sessions you agree to be responsible for ensuring you are practising in a 
                      suitable remote environment, and that you are healthy enough to participate.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Guidelines for Optimum Learning</h2>
              <div className="space-y-4">
                {[
                  "You will be sent a private invitation or link to a live streamed secure remote session. No links, passwords or access to any sessions should be shared with anyone else.",
                  "If you are using the technology for the first time, allow 15 minutes prior to the session to get set up properly.",
                  "Use an ethernet cable to connect your device to your router if possible. Close all other web browsers and data heavy applications so that your internet connection is secure and stable.",
                  "Mute alerts on your phone and the device you are using for the session.",
                  "Think about connecting your laptop to your TV via an HDMI cable to give you a bigger screen.",
                  "In a live streamed session, arrange your screen so that your teacher can see as much of you as possible. You will probably need to be a minimum of 6 feet away from your device.",
                  "Make sure you have everything ready beforehand including any props (blocks, belts, blankets, cushions), notebook and pen, and water.",
                  "Dress as you would if you were attending a face to face yoga class.",
                  "Make sure you have plenty of room to move your arms and legs and that the space around you is clear and safe.",
                  "In a live streamed session, keep your microphone muted when not speaking to avoid background noise.",
                  "If you need to speak to the teacher, use the chat or 'raise hand' button.",
                  "Enjoy the experience! Don't worry about being perfect. Be human, be authentic, be you."
                ].map((guideline, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10">
                    <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-gold font-semibold text-xs">{index + 1}</span>
                    </div>
                    <p className="text-neutral-700 text-sm">{guideline}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">GDPR Notice</h2>
              <p>
                If you are taking part in a live streamed session that is being recorded, you will be asked in advance to 
                give your consent to this, in accordance with GDPR legislation.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Questions?</h2>
              <p>
                If you have any questions about remote sessions, please don't hesitate to{" "}
                <Link to="/contact" className="text-gold hover:underline">get in touch</Link>.
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gold/20">
              <p className="text-xs text-neutral-500">
                This guidance is based on British Wheel of Yoga best practice guidelines.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RemoteSessionsPage;
